/**
 * Admin User Management Component
 *
 * Allows admins to view, add, edit, and deactivate users.
 * Manages work center assignments and role flags.
 */

import { useState, useEffect, useCallback } from "react";
import { apiUrl } from "../utils/apiBase";

const WORK_CENTERS = [
  { id: "PM", label: "Project Management" },
  { id: "PA", label: "Production Art" },
  { id: "OPTI", label: "OPTI/Finishing" },
  { id: "ASSEMBLY", label: "Assembly" },
  { id: "PAINT", label: "Paint" },
  { id: "3D MODEL", label: "3D Model" },
  { id: "FULFILLMNT", label: "Fulfillment" },
  { id: "QA", label: "Quality Assurance" },
];

// Check if we're on staging
const isStaging = typeof window !== 'undefined' && window.location.pathname.startsWith('/staging');

export default function AdminUserManagement({ onBackToApp }) {
  // State
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("active"); // active, inactive, all

  // Modal state
  const [editModal, setEditModal] = useState({ open: false, employee: null });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [formError, setFormError] = useState(null);

  // Staging sync state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Auth token
  const token = sessionStorage.getItem("msal.backendToken");

  // Load employees
  const loadEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const activeParam = activeFilter === "all" ? "false" : activeFilter === "active" ? "true" : "false";
      const url = activeFilter === "inactive"
        ? apiUrl("/admin/employees?active=false")
        : activeFilter === "all"
        ? apiUrl("/admin/employees?active=false")
        : apiUrl("/admin/employees");

      const [empRes, statsRes] = await Promise.all([
        fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(apiUrl("/admin/employees/stats"), {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (!empRes.ok) throw new Error("Failed to fetch employees");
      if (!statsRes.ok) throw new Error("Failed to fetch stats");

      const empData = await empRes.json();
      const statsData = await statsRes.json();

      // Filter based on active status
      let filtered = empData.employees || [];
      if (activeFilter === "active") {
        filtered = filtered.filter((e) => e.isActive);
      } else if (activeFilter === "inactive") {
        filtered = filtered.filter((e) => !e.isActive);
      }

      setEmployees(filtered);
      setStats(statsData);
    } catch (err) {
      console.error("Error loading employees:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, activeFilter]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  // Load staging sync status (only on staging)
  const loadStagingStatus = useCallback(async () => {
    if (!isStaging) return;

    try {
      const res = await fetch(apiUrl("/admin/staging/info"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSyncStatus(data);
      }
    } catch (err) {
      console.error("Error loading staging status:", err);
    }
  }, [token]);

  useEffect(() => {
    if (isStaging) {
      loadStagingStatus();
    }
  }, [loadStagingStatus]);

  // Handle sync from production
  const handleSyncFromProduction = async () => {
    if (!confirm("This will replace all staging data with production data. Continue?")) {
      return;
    }

    setSyncing(true);
    setSyncResult(null);

    try {
      const res = await fetch(apiUrl("/admin/staging/sync"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await res.json();

      if (res.ok) {
        setSyncResult({
          success: true,
          message: data.message,
          details: data.results,
        });
        // Reload employees after sync
        loadEmployees();
        loadStagingStatus();
      } else {
        setSyncResult({
          success: false,
          message: data.error || "Sync failed",
        });
      }
    } catch (err) {
      console.error("Error syncing from production:", err);
      setSyncResult({
        success: false,
        message: err.message,
      });
    } finally {
      setSyncing(false);
    }
  };

  // Filter employees by search query
  const filteredEmployees = employees.filter((emp) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      emp.displayName?.toLowerCase().includes(q) ||
      emp.azureEmail?.toLowerCase().includes(q) ||
      emp.id?.toLowerCase().includes(q)
    );
  });

  // Create/Update employee
  const handleSaveEmployee = async (formData) => {
    setProcessing(true);
    setFormError(null);

    try {
      const isEdit = !!editModal.employee;
      const url = isEdit
        ? apiUrl(`/admin/employees/${editModal.employee.id}`)
        : apiUrl("/admin/employees");
      const method = isEdit ? "PATCH" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to save employee");
      }

      setEditModal({ open: false, employee: null });
      await loadEmployees();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  // Deactivate employee
  const handleDeactivate = async (id) => {
    setProcessing(true);

    try {
      const res = await fetch(apiUrl(`/admin/employees/${id}`), {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to deactivate employee");
      }

      setDeleteConfirm(null);
      await loadEmployees();
    } catch (err) {
      console.error("Error deactivating employee:", err);
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  // Reactivate employee
  const handleReactivate = async (id) => {
    setProcessing(true);

    try {
      const res = await fetch(apiUrl(`/admin/employees/${id}/reactivate`), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to reactivate employee");
      }

      await loadEmployees();
    } catch (err) {
      console.error("Error reactivating employee:", err);
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="flex-1 bg-slate-200 overflow-auto">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Action Bar */}
        <div className="flex items-center justify-end mb-4">
          <button
            onClick={() => setEditModal({ open: true, employee: null })}
            className="px-4 py-2 text-sm bg-green-600 hover:bg-green-700 rounded transition-colors font-medium"
          >
            + Add User
          </button>
        </div>

        {/* Staging Sync Panel - only visible on staging */}
        {isStaging && (
          <div className="mb-6 bg-cyan-900/30 border border-cyan-500/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-cyan-600 font-semibold flex items-center gap-2">
                  <span>Staging Environment</span>
                  <span className="text-xs px-2 py-0.5 bg-cyan-500/20 rounded">
                    {syncStatus?.staging?.database || 'loading...'}
                  </span>
                </h2>
                <p className="text-gray-500 text-sm mt-1">
                  Sync data from production to refresh staging with real data.
                </p>
                {syncStatus && syncStatus.staging?.tableCounts && (
                  <p className="text-xs text-gray-500 mt-1">
                    Current rows: {Object.values(syncStatus.staging.tableCounts).reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0)} total
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                {syncResult && (
                  <div className={`text-sm px-3 py-1 rounded ${
                    syncResult.success
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {syncResult.message}
                  </div>
                )}
                <button
                  onClick={handleSyncFromProduction}
                  disabled={syncing}
                  className="px-4 py-2 text-sm bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded transition-colors font-medium flex items-center gap-2"
                >
                  {syncing ? (
                    <>
                      <span className="animate-spin">&#8635;</span>
                      Syncing...
                    </>
                  ) : (
                    <>
                      <span>&#8635;</span>
                      Sync from Production
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard label="Total Users" value={stats.total} />
            <StatCard label="Active" value={stats.active} color="green" />
            <StatCard label="PMs" value={stats.pms} color="blue" />
            <StatCard label="Admins" value={stats.admins} color="purple" />
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <input
            type="text"
            placeholder="Search by name, email, or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="px-3 py-2 text-sm bg-slate-800 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-purple-500 w-64"
          />
          <div className="flex gap-1">
            {["active", "inactive", "all"].map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  activeFilter === filter
                    ? "bg-purple-600 text-gray-900"
                    : "bg-slate-700 text-gray-700 hover:bg-slate-600"
                }`}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
          </div>
          <button
            onClick={loadEmployees}
            className="px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 rounded transition-colors"
          >
            Refresh
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 px-4 py-3 bg-red-500/20 border border-red-500/50 rounded text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading users...</div>
        ) : (
          /* Employee Table */
          <div className="bg-slate-800/50 rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-700/50 text-gray-700 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">ID</th>
                  <th className="px-4 py-3 font-medium">Work Centers</th>
                  <th className="px-4 py-3 font-medium">Roles</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredEmployees.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredEmployees.map((emp) => (
                    <tr key={emp.id} className="hover:bg-slate-200">
                      <td className="px-4 py-3 text-gray-900 font-medium">
                        {emp.displayName}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{emp.azureEmail}</td>
                      <td className="px-4 py-3 text-gray-500 font-mono text-xs">
                        {emp.id}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(emp.workCenters || []).slice(0, 3).map((wc) => (
                            <span
                              key={wc}
                              className="px-1.5 py-0.5 text-xs bg-slate-600/50 rounded"
                            >
                              {wc}
                            </span>
                          ))}
                          {(emp.workCenters || []).length > 3 && (
                            <span className="px-1.5 py-0.5 text-xs text-gray-500">
                              +{emp.workCenters.length - 3}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {emp.isPm && (
                            <span className="px-1.5 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded">
                              PM
                            </span>
                          )}
                          {emp.isAdmin && (
                            <span className="px-1.5 py-0.5 text-xs bg-purple-500/20 text-purple-600 rounded">
                              Admin
                            </span>
                          )}
                          {(emp.hasStagingAccess || emp.isAdmin) && (
                            <span className="px-1.5 py-0.5 text-xs bg-cyan-500/20 text-cyan-600 rounded">
                              Staging
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded ${
                            emp.isActive
                              ? "bg-green-500/20 text-green-400"
                              : "bg-slate-200/20 text-gray-500"
                          }`}
                        >
                          {emp.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => setEditModal({ open: true, employee: emp })}
                            className="px-2 py-1 text-xs bg-slate-600 hover:bg-white0 rounded transition-colors"
                          >
                            Edit
                          </button>
                          {emp.isActive ? (
                            <button
                              onClick={() => setDeleteConfirm(emp)}
                              className="px-2 py-1 text-xs bg-red-600/50 hover:bg-red-600 rounded transition-colors"
                            >
                              Deactivate
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReactivate(emp.id)}
                              disabled={processing}
                              className="px-2 py-1 text-xs bg-green-600/50 hover:bg-green-600 rounded transition-colors disabled:opacity-50"
                            >
                              Reactivate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit/Create Modal */}
      {editModal.open && (
        <EmployeeModal
          employee={editModal.employee}
          onClose={() => {
            setEditModal({ open: false, employee: null });
            setFormError(null);
          }}
          onSave={handleSaveEmployee}
          processing={processing}
          error={formError}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-lg p-6 w-full max-w-md border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Deactivate User?
            </h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to deactivate{" "}
              <strong>{deleteConfirm.displayName}</strong>? They will no longer
              be able to access the system.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeactivate(deleteConfirm.id)}
                disabled={processing}
                className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 rounded transition-colors disabled:opacity-50"
              >
                {processing ? "Deactivating..." : "Deactivate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Stat Card Component
function StatCard({ label, value, color = "gray" }) {
  const colorClasses = {
    gray: "bg-slate-700/50 text-gray-700",
    green: "bg-green-500/20 text-green-400",
    blue: "bg-blue-500/20 text-blue-400",
    purple: "bg-purple-500/20 text-purple-600",
  };

  return (
    <div className="bg-slate-800/50 rounded-lg border border-gray-200 p-4">
      <div className="text-sm text-gray-500 mb-1">{label}</div>
      <div className={`text-2xl font-bold ${colorClasses[color]}`}>{value}</div>
    </div>
  );
}

// Employee Edit/Create Modal
function EmployeeModal({ employee, onClose, onSave, processing, error }) {
  const isEdit = !!employee;

  const [formData, setFormData] = useState({
    id: employee?.id || "",
    azureEmail: employee?.azureEmail || "",
    displayName: employee?.displayName || "",
    firstName: employee?.firstName || "",
    lastName: employee?.lastName || "",
    workCenters: employee?.workCenters || [],
    isPm: employee?.isPm || false,
    isAdmin: employee?.isAdmin || false,
    hasStagingAccess: employee?.hasStagingAccess || false,
    hoursPerDay: employee?.hoursPerDay || 8,
    isActive: employee?.isActive !== false,
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleWorkCenter = (wc) => {
    setFormData((prev) => ({
      ...prev,
      workCenters: prev.workCenters.includes(wc)
        ? prev.workCenters.filter((w) => w !== wc)
        : [...prev.workCenters, wc],
    }));
  };

  const selectAllWorkCenters = () => {
    setFormData((prev) => ({
      ...prev,
      workCenters: WORK_CENTERS.map((wc) => wc.id),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-slate-800 rounded-lg w-full max-w-xl border border-gray-200 my-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEdit ? "Edit User" : "Add New User"}
          </h3>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Error Message */}
            {error && (
              <div className="px-4 py-3 bg-red-500/20 border border-red-500/50 rounded text-red-600 text-sm">
                {error}
              </div>
            )}

            {/* Employee ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Employee ID (JobBOSS) *
              </label>
              <input
                type="text"
                value={formData.id}
                onChange={(e) => handleChange("id", e.target.value)}
                disabled={isEdit}
                required
                className="w-full px-3 py-2 text-sm bg-slate-700 border border-gray-200 rounded text-gray-900 focus:outline-none focus:border-purple-500 disabled:opacity-50"
                placeholder="e.g., 47"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email (Azure AD) *
              </label>
              <input
                type="email"
                value={formData.azureEmail}
                onChange={(e) => handleChange("azureEmail", e.target.value)}
                required
                className="w-full px-3 py-2 text-sm bg-slate-700 border border-gray-200 rounded text-gray-900 focus:outline-none focus:border-purple-500"
                placeholder="e.g., john.doe@3dcolor.com"
              />
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleChange("firstName", e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-slate-700 border border-gray-200 rounded text-gray-900 focus:outline-none focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleChange("lastName", e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-slate-700 border border-gray-200 rounded text-gray-900 focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>

            {/* Display Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Name *
              </label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => handleChange("displayName", e.target.value)}
                required
                className="w-full px-3 py-2 text-sm bg-slate-700 border border-gray-200 rounded text-gray-900 focus:outline-none focus:border-purple-500"
                placeholder="e.g., John Doe"
              />
            </div>

            {/* Work Centers */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Work Centers
                </label>
                <button
                  type="button"
                  onClick={selectAllWorkCenters}
                  className="text-xs text-purple-600 hover:text-purple-600"
                >
                  Select All
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {WORK_CENTERS.map((wc) => (
                  <label
                    key={wc.id}
                    className="flex items-center gap-2 px-3 py-2 bg-slate-700/50 rounded cursor-pointer hover:bg-slate-700"
                  >
                    <input
                      type="checkbox"
                      checked={formData.workCenters.includes(wc.id)}
                      onChange={() => toggleWorkCenter(wc.id)}
                      className="w-4 h-4 rounded border-gray-500 bg-slate-600 text-purple-500 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">{wc.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Roles */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Roles
              </label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isPm}
                    onChange={(e) => handleChange("isPm", e.target.checked)}
                    className="w-4 h-4 rounded border-gray-500 bg-slate-600 text-blue-500 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Project Manager</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isAdmin}
                    onChange={(e) => handleChange("isAdmin", e.target.checked)}
                    className="w-4 h-4 rounded border-gray-500 bg-slate-600 text-purple-500 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">Admin</span>
                </label>
              </div>
            </div>

            {/* Staging Access */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Staging Environment
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.hasStagingAccess || formData.isAdmin}
                  onChange={(e) => handleChange("hasStagingAccess", e.target.checked)}
                  disabled={formData.isAdmin}
                  className="w-4 h-4 rounded border-gray-500 bg-slate-600 text-cyan-500 focus:ring-cyan-500 disabled:opacity-50"
                />
                <span className="text-sm text-gray-700">
                  Staging Access
                  {formData.isAdmin && (
                    <span className="text-xs text-gray-500 ml-1">(Admins always have access)</span>
                  )}
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Allow access to the staging/testing environment
              </p>
            </div>

            {/* Hours Per Day */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hours Per Day
              </label>
              <input
                type="number"
                value={formData.hoursPerDay}
                onChange={(e) =>
                  handleChange("hoursPerDay", parseFloat(e.target.value) || 8)
                }
                min="1"
                max="24"
                step="0.5"
                className="w-24 px-3 py-2 text-sm bg-slate-700 border border-gray-200 rounded text-gray-900 focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={processing}
              className="px-4 py-2 text-sm bg-green-600 hover:bg-green-700 rounded transition-colors font-medium disabled:opacity-50"
            >
              {processing ? "Saving..." : isEdit ? "Save Changes" : "Add User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
